import { useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { BrandSelect } from '../lib/brands.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Panel,
  StatusBadge,
} from '../components/ui.js';
import '../screens-2a.css';
import { TRIP_STATE_LABEL, label } from '../lib/labels.js';
import { date, num } from '../lib/format.js';
import type { Rights } from '../lib/session.js';
import { FacebookExport } from '../components/FacebookExport.js';
import { SegmentTemplates } from './SegmentTemplates.js';
import { SegmentBuilder } from './SegmentBuilder.js';
import { SegmentProposals } from './SegmentProposals.js';

/**
 * Segmenty zákazníků (SPEC-2 kap. 14). Skládají se z nabízených podmínek —
 * žádné volné dotazy. Náhled ukáže počet dřív, než segment vznikne.
 */

interface Definition {
  series?: string;
  raceId?: string;
  interest?: 'purchased' | 'viewed' | 'any';
  boughtWithinMonths?: number;
  neverBought?: boolean;
  tripState?: string;
  engagedWithinDays?: number;
  dormantForDays?: number;
  webActiveWithinDays?: number;
  webEngagedWithinDays?: number;
  webMinPageviews?: number;
  webMinPageviewsWithinDays?: number;
  cameFromMessageCode?: string;
  cameFromWithinDays?: number;
  webKeyEvent?: string;
  webKeyEventWithinDays?: number;
  language?: string;
  seller?: string;
  consent?: string;
  excludeHoldout?: boolean;
}

interface SegmentRow {
  code: string;
  name: string;
  description: string | null;
  definition: Definition;
  enabled: boolean;
  pushToProvider: boolean;
  memberCount: number;
  mirrorCount: number | null;
  mirrorCountAfterExclusions: number | null;
  lastComputedAt: string | null;
  field: string;
  popis: string;
}

const TRIP_STATES = [
  '',
  'scheduled',
  'awaiting_date',
  'tickets_pending',
  'ready',
  'race_week',
  'post_race',
  'closed',
];

const emptyDefinition = (): Definition => ({ interest: 'any' });

export function Segments({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const canEdit = rights.can('edit_catalogue');

  const query = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ items: SegmentRow[] }>('/api/segments'),
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [definition, setDefinition] = useState<Definition>(emptyDefinition);
  const [push, setPush] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SegmentRow | null>(null);

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['segments'] });
  };

  const preview = useMutation({
    mutationFn: () =>
      api.post<{ members: number; popis: string }>('/api/segments/preview', { definition }),
    onSuccess: (data) => setNote(`Do segmentu by spadlo ${data.members} kontaktů — ${data.popis}.`),
    onError: (err: Error) => setNote(err.message),
  });

  const save = useMutation({
    mutationFn: () =>
      api.put<{ members: number }>(`/api/segments/${encodeURIComponent(code)}`, {
        code,
        name: name || code,
        definition,
        enabled: true,
        pushToProvider: push,
      }),
    onSuccess: (data) => {
      setNote(`Segment uložen, má ${data.members} kontaktů.`);
      setCode('');
      setName('');
      setDefinition(emptyDefinition());
      setPush(false);
      refresh();
    },
    onError: (err: Error) => setNote(err.message),
  });

  const recompute = useMutation({
    mutationFn: () => api.post('/api/segments/recompute', {}),
    onSuccess: () => {
      setNote('Segmenty přepočítané.');
      refresh();
    },
  });

  const pushNow = useMutation({
    mutationFn: () => api.post<{ contacts: number }>('/api/segments/push', {}),
    onSuccess: (data) => setNote(`Předáno do SmartEmailingu pro ${data.contacts} kontaktů.`),
    onError: (err: Error) => setNote(err.message),
  });

  const remove = useMutation({
    mutationFn: (segmentCode: string) =>
      api.del(`/api/segments/${encodeURIComponent(segmentCode)}`),
    onSuccess: () => {
      setNote('Segment smazán.');
      refresh();
    },
    onError: (err: Error) => setNote(err.message),
  });

  if (query.isLoading) return <LoadingState what="segmenty" />;
  if (query.isError)
    return (
      <ErrorState
        title="Segmenty se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );

  const set = (patch: Partial<Definition>): void => setDefinition({ ...definition, ...patch });
  const numeric = (value: string): number | undefined => (value === '' ? undefined : Number(value));

  return (
    <>
      {note ? (
        <p className="hint" role="status">
          {note}
        </p>
      ) : null}

      <Panel
        title="Segmenty"
        action={
          canEdit ? (
            <div className="row">
              <button className="btn" onClick={() => recompute.mutate()}>
                Přepočítat
              </button>
              <button className="btn" onClick={() => pushNow.mutate()}>
                Předat do SmartEmailingu
              </button>
            </div>
          ) : null
        }
      >
        <p className="note">
          Segment je množina kontaktů podle chování a zájmu. Hub ho přepočítává každou noc a u
          zapnutých segmentů píše členství do pole kontaktu v SmartEmailingu (1 nebo 0). Ve flow jde
          segment použít jako podmínku <code>segment:&lt;kód&gt;</code>.
        </p>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Kód</th>
                <th>Název</th>
                <th>Definice</th>
                <th className="n">Kontaktů v hubu</th>
                <th className="n">V zrcadle SE (po výlukách)</th>
                <th>Pole ve SmartEmailingu</th>
                <th>Spočítáno</th>
                <th>
                  <span className="sr-only">Akce</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {query.data?.items.map((seg) => (
                <tr key={seg.code}>
                  <td>
                    <code>{seg.code}</code>
                  </td>
                  <td>{seg.name}</td>
                  <td className="note">{seg.popis}</td>
                  <td className="n">{num(seg.memberCount)}</td>
                  <td className="n">
                    {seg.mirrorCount === null
                      ? '—'
                      : `${num(seg.mirrorCount)} (${num(seg.mirrorCountAfterExclusions ?? 0)})`}
                  </td>
                  <td>
                    {seg.pushToProvider ? (
                      <code>{seg.field}</code>
                    ) : (
                      <StatusBadge tone="neu" icon="info">
                        Neposílá se
                      </StatusBadge>
                    )}
                  </td>
                  <td>{seg.lastComputedAt ? date(seg.lastComputedAt) : '—'}</td>
                  <td>
                    {canEdit ? (
                      <FacebookExport
                        path={`/api/segments/${encodeURIComponent(seg.code)}/export-facebook`}
                        label="Exportovat pro Facebook"
                      />
                    ) : null}
                    {canEdit ? (
                      <button className="btn dng" onClick={() => setDeleting(seg)}>
                        Smazat
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {(query.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Zatím žádné segmenty">
            {canEdit
              ? 'Segment vytvoříte ve formuláři níže. Nejdřív si můžete nechat spočítat náhled.'
              : 'Segmenty zakládá ten, kdo spravuje katalog zpráv.'}
          </EmptyState>
        ) : null}
      </Panel>

      <SegmentTemplates canEdit={canEdit} />

      <SegmentProposals canEdit={canEdit} />

      {canEdit ? <SegmentBuilder onSaved={(text) => setNote(text)} /> : null}

      {canEdit ? (
        <details>
          <summary>Všechny podmínky (pro pokročilé)</summary>
          <Panel title="Nový segment — technická podoba">
            <div className="row">
              <Field label="Kód (malá písmena, číslice, podtržítko)">
                <input
                  value={code}
                  placeholder="f1_kupujici"
                  onChange={(e) => setCode(e.target.value)}
                />
              </Field>
              <Field label="Název">
                <input
                  value={name}
                  placeholder="Kupující F1"
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
            </div>

            <div className="row">
              <Field label="Série">
                <select
                  value={definition.series ?? ''}
                  onChange={(e) => set({ series: e.target.value || undefined })}
                >
                  <option value="">kterákoli</option>
                  <option value="f1">F1</option>
                  <option value="motogp">MotoGP</option>
                </select>
              </Field>
              <Field label="Konkrétní závod">
                <input
                  value={definition.raceId ?? ''}
                  placeholder="f1-monaco-2027"
                  onChange={(e) => set({ raceId: e.target.value || undefined })}
                />
              </Field>
              <Field label="Zájem se pozná">
                <select
                  value={definition.interest ?? 'any'}
                  onChange={(e) => set({ interest: e.target.value as Definition['interest'] })}
                >
                  <option value="any">nákupem nebo návštěvou webu</option>
                  <option value="purchased">jen nákupem</option>
                  <option value="viewed">jen návštěvou webu</option>
                </select>
              </Field>
            </div>

            <div className="row">
              <Field label="Koupil za posledních N měsíců">
                <input
                  type="number"
                  value={definition.boughtWithinMonths ?? ''}
                  onChange={(e) => set({ boughtWithinMonths: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Stav cesty">
                <select
                  value={definition.tripState ?? ''}
                  onChange={(e) => set({ tripState: e.target.value || undefined })}
                >
                  {TRIP_STATES.map((state) => (
                    <option key={state} value={state}>
                      {state === '' ? 'Kterýkoli' : label(TRIP_STATE_LABEL, state)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Reagoval za N dní">
                <input
                  type="number"
                  value={definition.engagedWithinDays ?? ''}
                  onChange={(e) => set({ engagedWithinDays: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Nereagoval N dní">
                <input
                  type="number"
                  value={definition.dormantForDays ?? ''}
                  onChange={(e) => set({ dormantForDays: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Na webu za N dní">
                <input
                  type="number"
                  value={definition.webActiveWithinDays ?? ''}
                  onChange={(e) => set({ webActiveWithinDays: numeric(e.target.value) })}
                />
              </Field>
            </div>

            <div className="row">
              <Field
                label="Aktivní na webu za N dní"
                hint="Aktivní návštěva = dost aktivního času nebo klíčová akce. Jen lidé, kteří dali souhlas s měřením."
              >
                <input
                  type="number"
                  min={1}
                  value={definition.webEngagedWithinDays ?? ''}
                  onChange={(e) => set({ webEngagedWithinDays: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Aspoň N zobrazených stránek">
                <input
                  type="number"
                  min={1}
                  value={definition.webMinPageviews ?? ''}
                  onChange={(e) => set({ webMinPageviews: numeric(e.target.value) })}
                />
              </Field>
              <Field label="…za posledních N dní" hint="Výchozí 30.">
                <input
                  type="number"
                  min={1}
                  value={definition.webMinPageviewsWithinDays ?? ''}
                  onChange={(e) => set({ webMinPageviewsWithinDays: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Přišel ze zprávy s kódem" hint="Například S06 nebo M01.">
                <input
                  value={definition.cameFromMessageCode ?? ''}
                  maxLength={20}
                  onChange={(e) =>
                    set({ cameFromMessageCode: e.target.value.toUpperCase() || undefined })
                  }
                />
              </Field>
              <Field label="…za posledních N dní" hint="Výchozí 90.">
                <input
                  type="number"
                  min={1}
                  value={definition.cameFromWithinDays ?? ''}
                  onChange={(e) => set({ cameFromWithinDays: numeric(e.target.value) })}
                />
              </Field>
              <Field label="Klíčová akce na webu" hint="Název akce, například begin_checkout.">
                <input
                  value={definition.webKeyEvent ?? ''}
                  maxLength={40}
                  onChange={(e) => set({ webKeyEvent: e.target.value.toLowerCase() || undefined })}
                />
              </Field>
              <Field label="…za posledních N dní" hint="Výchozí 30.">
                <input
                  type="number"
                  min={1}
                  value={definition.webKeyEventWithinDays ?? ''}
                  onChange={(e) => set({ webKeyEventWithinDays: numeric(e.target.value) })}
                />
              </Field>
            </div>

            <div className="row">
              <Field label="Jazyk">
                <input
                  value={definition.language ?? ''}
                  placeholder="cs"
                  onChange={(e) => set({ language: e.target.value || undefined })}
                />
              </Field>
              <Field label="Značka">
                <BrandSelect
                  value={definition.seller ?? ''}
                  onChange={(code) => set({ seller: code || undefined })}
                  allLabel="všechny"
                />
              </Field>
              <Field label="Souhlas">
                <select
                  value={definition.consent ?? ''}
                  onChange={(e) => set({ consent: e.target.value || undefined })}
                >
                  <option value="">nerozhoduje</option>
                  <option value="granted">má souhlas</option>
                  <option value="not_given">nemá souhlas</option>
                  <option value="withdrawn">odvolal</option>
                </select>
              </Field>
            </div>

            <div className="row">
              <label className="row">
                <input
                  type="checkbox"
                  checked={definition.neverBought ?? false}
                  onChange={(e) => set({ neverBought: e.target.checked || undefined })}
                />
                <span className="note">nikdy nekoupil</span>
              </label>
              <label className="row">
                <input
                  type="checkbox"
                  checked={definition.excludeHoldout ?? false}
                  onChange={(e) => set({ excludeHoldout: e.target.checked || undefined })}
                />
                <span className="note">bez kontrolní skupiny</span>
              </label>
              <label className="row">
                <input type="checkbox" checked={push} onChange={(e) => setPush(e.target.checked)} />
                <span className="note">posílat do SmartEmailingu</span>
              </label>
            </div>

            <div className="row">
              <button className="btn" onClick={() => preview.mutate()}>
                Spočítat náhled
              </button>
              <button className="btn pri" disabled={!code} onClick={() => save.mutate()}>
                Uložit segment
              </button>
            </div>
          </Panel>
        </details>
      ) : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Smazat segment?"
        danger
        confirmLabel="Smazat segment"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove.mutate(deleting.code);
          setDeleting(null);
        }}
      >
        Segment {deleting?.name} se smaže. Zprávy nebo flow, které se na něj odkazují, přestanou
        fungovat.
      </ConfirmDialog>
    </>
  );
}
